import { Controller } from "@hotwired/stimulus"

// Simple controller for showing immediate preview of selected image files
// Usage:
//   <div data-controller="image-preview">
//     <div data-image-preview-target="preview" class="hidden">
//       <img data-image-preview-target="image">
//       <span data-image-preview-target="filename"></span>
//       <button data-action="click->image-preview#clear">Remove</button>
//     </div>
//     <div data-image-preview-target="dropzone">
//       <input type="file" data-image-preview-target="input" data-action="change->image-preview#preview">
//     </div>
//   </div>

export default class extends Controller {
  static targets = [
    "input",
    "image",
    "filename",
    "preview",
    "dropzone",
    "removeFlag",
    "stagedInput",
    "lightbox",
  ]

  preview(event) {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Bitte wählen Sie eine Bilddatei aus.")
      this.clear()
      return
    }

    // Reset remove flag since we're uploading a new image
    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = "0"
    }

    // Read and display preview
    const reader = new FileReader()
    reader.onload = (e) => {
      if (this.hasImageTarget) {
        this.imageTarget.src = e.target.result
      }
      // The lightbox reads its url from the data attribute, so a file picked but not
      // yet saved can be viewed full size like a stored one.
      this.setLightbox(e.target.result, file.name)
      if (this.hasFilenameTarget) {
        this.filenameTarget.textContent = file.name
      }
      if (this.hasPreviewTarget) {
        this.previewTarget.classList.remove("hidden")
      }
      if (this.hasDropzoneTarget) {
        this.dropzoneTarget.classList.add("hidden")
      }
    }
    reader.readAsDataURL(file)
  }

  clear(event) {
    if (event) event.preventDefault()

    this.dropPendingUploads()

    // Hide preview, show dropzone
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("hidden")
    }

    // Clear image src
    if (this.hasImageTarget) {
      this.imageTarget.src = ""
    }
    this.setLightbox("", "")

    // Set remove flag if present (for existing attachments)
    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = "1"
    }
  }

  // For existing images - handle removal
  remove(event) {
    if (event) event.preventDefault()

    this.dropPendingUploads()

    // Set remove flag
    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = "1"
    }

    this.setLightbox("", "")

    // Hide preview, show dropzone
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("hidden")
    }
  }

  // Points the lightbox link at the given source. A blank url disables it:
  // PreviewOverlayBase#open swallows the click and returns.
  setLightbox(url, filename) {
    if (!this.hasLightboxTarget) return

    const link = this.lightboxTarget
    link.href = url || "#"
    link.dataset.attachmentPreviewUrlValue = url
    link.dataset.attachmentPreviewFilenameValue = filename
    link.dataset.attachmentPreviewDownloadUrlValue = url
  }

  // A picked file and a staged blob carried over a rejected save both post under the
  // same field name, and an upload beats the remove flag on the server. Removing the
  // image therefore has to take them out of the submit, not just set the flag.
  dropPendingUploads() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    this.stagedInputTargets.forEach((field) => field.remove())
  }
}
